import React, { useState, useEffect } from 'react';
import { Heart, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export const FavoritesListModal = ({ 
  isOpen, 
  onClose, 
  userLists = [],
  onCreateList,
  onSelectList,
  photoId,
  userEmail // Email from gallery access gate
}) => {
  const [selectedListId, setSelectedListId] = useState(userLists[0]?.id || '');
  const [isCreatingNew, setIsCreatingNew] = useState(userLists.length === 0);
  const [newListName, setNewListName] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset to "Choose Existing" when modal opens if lists are available
  useEffect(() => {
    if (isOpen) {
      setIsCreatingNew(userLists.length === 0);
      setSelectedListId(userLists[0]?.id || '');
      setNewListName('');
    }
  }, [isOpen, userLists]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isCreatingNew) {
        // Create new list and add photo using email from access gate
        await onCreateList(newListName, userEmail, photoId);
      } else {
        // Add photo to existing list
        await onSelectList(selectedListId, photoId);
      }
      onClose();
    } catch (error) {
      console.error('Error saving favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] font-tiq-body text-white border-0" style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(191,33,47,0.15)' }}>
              <Heart className="w-5 h-5 text-[#BF212F]" fill="#BF212F" />
            </div>
            <div>
              <DialogTitle className="font-tiq-display uppercase" style={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.015em' }}>Add to favourites</DialogTitle>
              <DialogDescription className="mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {userLists.length > 0 ? 'Choose an existing list or create a new one' : 'Create your first favourites list'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {userLists.length > 0 && (
            <>
              {/* Toggle */}
              <div className="inline-flex w-full p-1" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 10 }}>
                {[{ k: false, label: 'Choose existing' }, { k: true, label: 'Create new' }].map((t) => {
                  const on = isCreatingNew === t.k;
                  return (
                    <button
                      key={String(t.k)}
                      onClick={() => setIsCreatingNew(t.k)}
                      className="font-tiq-heading flex-1 inline-flex items-center justify-center gap-2"
                      style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', background: on ? '#BF212F' : 'transparent', color: on ? '#fff' : 'rgba(255,255,255,0.6)' }}
                    >
                      {t.k && <Plus className="w-4 h-4" />}{t.label}
                    </button>
                  );
                })}
              </div>

              {!isCreatingNew && (
                <div className="space-y-3">
                  <Label className="font-tiq-heading text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>Select a list</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userLists.map((list) => {
                      const on = selectedListId === list.id;
                      return (
                        <button
                          key={list.id}
                          onClick={() => setSelectedListId(list.id)}
                          className="w-full flex items-center justify-between text-left transition-all"
                          style={{ padding: 14, borderRadius: 12, background: on ? 'rgba(191,33,47,0.12)' : 'rgba(255,255,255,0.04)', border: on ? '2px solid #BF212F' : '2px solid rgba(255,255,255,0.1)' }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(191,33,47,0.15)' }}>
                              <Heart className="w-5 h-5 text-[#BF212F]" fill="currentColor" />
                            </div>
                            <div>
                              <div className="font-tiq-heading font-semibold text-white">{list.list_name}</div>
                              <div className="font-tiq-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{list.photo_count} photo{list.photo_count !== 1 ? 's' : ''}</div>
                            </div>
                          </div>
                          <div className="flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: '50%', border: on ? 'none' : '2px solid rgba(255,255,255,0.25)', background: on ? '#BF212F' : 'transparent' }}>
                            {on && <div style={{ width: 7, height: 7, background: '#fff', borderRadius: '50%' }} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {isCreatingNew && (
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 16 }}>
              <Label htmlFor="listName" className="font-tiq-heading text-sm font-semibold mb-2 block" style={{ color: 'rgba(255,255,255,0.75)' }}>Name your list</Label>
              <input
                id="listName"
                placeholder="e.g. Best moments, Team photos"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter' && newListName.trim()) handleSubmit(); }}
                autoFocus
                className="w-full px-3 py-2.5 rounded-lg focus:outline-none"
                style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff' }}
              />
              <div className="flex items-center gap-2 mt-3" style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                <div style={{ width: 5, height: 5, background: 'rgba(255,255,255,0.4)', borderRadius: '50%' }}></div>
                <p>Saved to {userEmail}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onClose} disabled={loading} className="font-tiq-heading" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: 'transparent' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || (isCreatingNew && !newListName.trim())}
            className="font-tiq-heading text-white"
            style={{ background: '#BF212F', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: loading || (isCreatingNew && !newListName.trim()) ? 0.5 : 1 }}
          >
            {loading ? 'Adding…' : isCreatingNew ? 'Create & add photo' : 'Add to list'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
